import React, { FC, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { RadioFilter } from '../RadioFilter';
import { CheckboxFilter } from '../CheckboxFilter';
import { useData } from '@hooks/useData';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { SITE_URLS } from '@constants';
import { useFilters } from '@hooks/useFilters';
import { useFilterBuilder } from '@hooks/useFilterBuilder';
import { flattenCategories } from '@utils/helper';
import { useFilterBuilderText } from '@hooks/useFilterBuilderText';
import { useGetAllCategoriesWithServices } from '@services/useGetAllCategoriesWithServices';
import { useGetServiceStores } from '@services/useGetServiceStores';
import { useGetZones } from '@services/useGetZones';
import { Modules, useModule } from '@hooks/useModule';
import { ServiceCardProps } from '@components/molecules/ServiceCard';
import { FavoriteFilter } from '../FavoriteFilter';

export interface FilterSidebarServicesProps {
    isSearchResultsPage: boolean;
    pageToRequest: number;
    checkPageToRequest: () => void;
}

export const FilterSidebarServices: FC<FilterSidebarServicesProps> = ({
    isSearchResultsPage,
    pageToRequest,
    checkPageToRequest,
}) => {
    const location = useLocation();
    const { navigate } = useCustomNavigate();
    const { searchQuery, categoryUuid } = useParams();
    const { request: requestAllCategoriesWithServices } = useGetAllCategoriesWithServices();
    const { request: requestServiceStores } = useGetServiceStores();
    const {
        login,
        allCategoriesWithServices,
        isLoadingAllCategoriesWithProducts,
        serviceStores,
        isLoadingServiceStores,
        setServiceStores,
    } = useData();
    const { appliedFilters, results, setResults, currentPage, setCurrentPage } = useFilters();
    const { isModuleActive } = useModule();
    const { t } = useTranslation();

    const translations = {
        radioSearchProducts: t('filter-sidebar-services-component.radio-products-search.label'),
        radioSearchServices: t('filter-sidebar-services-component.radio-services-search.label'),
    };

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this page
        return () => {
            setResults([]);
            setServiceStores(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        !allCategoriesWithServices && !isLoadingAllCategoriesWithProducts && requestAllCategoriesWithServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allCategoriesWithServices]);

    const formRef = useRef<HTMLFormElement>(null);

    const queryParams = new URLSearchParams(location.search);
    const getTextParam = queryParams.get('text') || '';
    const getCategoriesParam = queryParams.get('categories') || '';
    const getOrderParam = queryParams.get('order') || '';
    const getZonesParam = queryParams.get('zones') || '';
    const favoriteParam = queryParams.get('favorites') === 'favorite-only';

    const fetchServices = (page: number) => {
        setCurrentPage(page);

        // prevent calls for pages that don't exist
        if (serviceStores?.pagination?.total_pages && page > serviceStores?.pagination?.total_pages) {
            return;
        }

        // NOTE: if categoryUuid is defined, means user is in services page associated with a service
        // if not, means he is either in general services page, or service search results page
        const categoriesUuids = categoryUuid ? [categoryUuid] : getCategoriesParam ? getCategoriesParam.split(',') : [];
        // NOTE: if searchQuery is defined, means user is in products search results page
        // if not, means he is either in general products page, or products page associated with a category
        const textSearch = searchQuery || getTextParam;
        const zonesUuids = getZonesParam ? getZonesParam.split(',') : [];

        requestServiceStores({
            textSearch: textSearch,
            categories_uuid: categoriesUuids,
            zone_uuid: zonesUuids,
            page: page,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            order: getOrderParam as any,
            favorite: favoriteParam,
        });
    };

    // request more Services from next page
    useEffect(() => {
        pageToRequest > currentPage && !isLoadingServiceStores && fetchServices(currentPage + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageToRequest]);

    // request first page whenever a filter changes
    const submitForm = () => fetchServices(1);

    useEffect(() => {
        !isLoadingServiceStores && formRef?.current?.requestSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters, searchQuery, categoryUuid]);

    // every time the endpoint is called, append the data to results constant
    // if the page being called is the first page, reset results
    useEffect(() => {
        setResults((previous: ServiceCardProps[]) => {
            const currentPageData = (serviceStores?.data || []).map((item) => {
                return {
                    ...item,
                    logo: {
                        src: item.logo?.src,
                        type: item.logo?.type,
                        width: item.logo?.width,
                        height: item.logo?.height,
                        isDefaultLogo: item.logo?.is_default_logo,
                    },
                    initialFavorite: item.favorite || false,
                };
            });

            return serviceStores?.pagination?.current_page === 1 ? currentPageData : [...previous, ...currentPageData];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceStores]);

    // manually check if next page should be requested
    // in case user's screen is big and isn't possible to scroll to trigger next page request
    useEffect(() => {
        checkPageToRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results.length]);

    return (
        <Form formRef={formRef} classes="d-flex flex-column gap-4" onSubmit={submitForm}>
            {isSearchResultsPage ? (
                <RadioFilter
                    options={[
                        {
                            id: 'product-option',
                            label: translations.radioSearchProducts,
                            value: 'product-search',
                            onClick: () => navigate(`/${SITE_URLS.productsSearch.replace(':searchQuery', `${searchQuery}`)}`),
                        },
                        {
                            id: 'service-option',
                            label: translations.radioSearchServices,
                            checked: true,
                            value: 'service-search',
                            onClick: () => navigate(`/${SITE_URLS.servicesSearch.replace(':searchQuery', `${searchQuery}`)}`),
                        },
                    ]}
                />
            ) : (
                <TextFilter />
            )}

            <>{isModuleActive(Modules.favorite) && login !== null && <FavoriteFilter />}</>

            <CategoriesFilters />

            <ZonesFilter />
        </Form>
    );
};

const TextFilter: FC = () => {
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        title: t('filter-sidebar-services-component.filter-text-search.title'),
        placeholder: t('filter-sidebar-services-component.text-search.placeholder'),
    };

    const paramName = 'text';
    const title = translations.title;

    const { search, setSearch } = useFilterBuilderText({
        paramName,
        appliedValue: appliedFilters.find((item) => item.label === title)?.values[0].name || '',
        setFilterValues: (data) => setFilterValues(title, data),
    });

    return (
        <Input
            type={InputTypes.text}
            variant={InputVariants.searchInput}
            placeholder={translations.placeholder}
            value={search || ''}
            onChange={(value) => setSearch(value)}
        />
    );
};

const CategoriesFilters: FC = () => {
    const { t } = useTranslation();
    const { allCategoriesWithServices } = useData();
    const { appliedFilters, setFilterValues } = useFilters();
    const { request: requestAllCategoriesWithServices } = useGetAllCategoriesWithServices();

    const translations = {
        title: t('filter-sidebar-services-component.checkbox-categories.title'),
    };

    const paramName = 'categories';
    const title = translations.title;

    const { getOptions, toggleValue } = useFilterBuilder([
        {
            paramName,
            labelField: 'name',
            data: { data: flattenCategories(allCategoriesWithServices?.data || []) },
            requestData: requestAllCategoriesWithServices,
            appliedValues: appliedFilters.find((item) => item.label === title)?.values.map((value) => value.name) || [],
            setFilterValues: (data) => setFilterValues(title, data),
        },
    ]);

    const options = getOptions(paramName);

    return (
        <>
            {allCategoriesWithServices && (
                <CheckboxFilter
                    title={translations.title}
                    options={options}
                    onChange={(value) => toggleValue(paramName, value)}
                />
            )}
        </>
    );
};

const ZonesFilter: FC = () => {
    const { allZones } = useData();
    const { request: requestAllZones } = useGetZones();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();
    const { isModuleActive } = useModule();

    const translations = {
        title: t('filter-sidebar-merchants-component.zones.title'),
    };

    useEffect(() => {
        if (!allZones && isModuleActive(Modules.zones)) {
            requestAllZones();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allZones]);

    const paramName = 'zones';
    const title = translations.title;

    const { getOptions, toggleValue } = useFilterBuilder(
        allZones
            ? [
                  {
                      paramName,
                      labelField: 'name',
                      data: { data: allZones.data || [] },
                      requestData: null,
                      appliedValues: appliedFilters.find((item) => item.label === title)?.values.map((v) => v.name) || [],
                      setFilterValues: (data) => setFilterValues(title, data),
                  },
              ]
            : []
    );

    const options = getOptions(paramName);

    return (
        <>
            {isModuleActive(Modules.zones) && allZones && options && (
                <CheckboxFilter title={title} options={options} onChange={(value) => toggleValue(paramName, value)} />
            )}
        </>
    );
};
